import React from 'react';

/**
 * Vivid gradient shared by the conversational-search glow and auto-on trace
 * affordances. Raw hex because SVG stops can't take Tailwind classes.
 */
export const GLOW_PURPLE = '#9952e0';
export const GLOW_PINK = '#ff66e6';
export const GLOW_VIOLET = '#b357e1';

/**
 * The gradient line and blur below were designed against a 141×89 canvas
 * (the search-page toggle). Other canvas sizes scale the gradient line
 * proportionally so the color blooms land in the same relative spots.
 */
const REFERENCE_CANVAS = { width: 141, height: 89 };
const GRADIENT_LINE = { x1: 235.242, y1: -29.415, x2: 6.14331, y2: 5.56662 };

// Def ids and animation classes are suffixed per instance so one trace can
// never resolve another's <defs> or animation duration. Render-time ids are
// safe here — the trace mounts only on client-side hover, never during SSR,
// so there is no server/client id pairing to preserve.
let traceInstanceCounter = 0;

interface AutoOnHoverTraceProps {
  /** The auto-on hover delay — the draw completes exactly when auto-on fires. */
  durationMs: number;
  /**
   * Canvas is larger than the button it decorates so the blurred stroke can
   * bleed past the button's edges.
   */
  canvasWidth: number;
  canvasHeight: number;
  /**
   * Fully closed pill outline (no gap) in canvas coordinates, surrounding
   * the button the trace plays over.
   */
  pillPath: string;
  /** Negative insets that centre the canvas over the button. */
  offsetLeft: number;
  offsetTop: number;
}

/**
 * Progress trace shown while the auto-on hover timer is counting down: the
 * button's outline draws on progressively from nothing to a complete ring,
 * finishing exactly when `durationMs` (the auto-on hover delay) elapses — so
 * the closed outline lands at the same instant conversational search
 * auto-enables.
 *
 * The path uses `pathLength={100}` so the draw is expressed in normalized
 * units regardless of the real geometry: a dasharray of one full length with
 * the dashoffset animating from full to zero reveals the stroke end-to-end.
 *
 * Rendered behind the button (`-z-10` inside the caller's isolated stacking
 * context) so only the portion bleeding past the button's edges is visible.
 * Positioned with inline insets because translate utilities hit the Tailwind
 * v2/v4 conflict on composed pages.
 */
export default function AutoOnHoverTrace({
  durationMs,
  canvasWidth,
  canvasHeight,
  pillPath,
  offsetLeft,
  offsetTop,
}: AutoOnHoverTraceProps): React.ReactElement {
  const gradientScaleX = canvasWidth / REFERENCE_CANVAS.width;
  const gradientScaleY = canvasHeight / REFERENCE_CANVAS.height;

  const [instanceId] = React.useState(() => {
    traceInstanceCounter += 1;
    return traceInstanceCounter;
  });
  const gradientId = `ai-trace-gradient-${instanceId}`;
  const blurId = `ai-trace-blur-${instanceId}`;
  const strokeClass = `ai-trace-stroke-${instanceId}`;
  const drawKeyframes = `ai-trace-draw-${instanceId}`;

  return (
    <svg
      aria-hidden="true"
      width={canvasWidth}
      height={canvasHeight}
      viewBox={`0 0 ${canvasWidth} ${canvasHeight}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ left: `${offsetLeft}px`, top: `${offsetTop}px` }}
      className="ai-trace-svg pointer-events-none absolute -z-10"
    >
      <style>{`
        @keyframes ${drawKeyframes} {
          from { stroke-dashoffset: 100; }
          to { stroke-dashoffset: 0; }
        }
        .${strokeClass} {
          stroke-dasharray: 100;
          stroke-dashoffset: 100;
          animation: ${drawKeyframes} ${durationMs}ms linear forwards;
        }
        @media (prefers-reduced-motion: reduce) {
          .${strokeClass} { animation: none; stroke-dashoffset: 0; }
        }
      `}</style>
      <defs>
        <linearGradient
          id={gradientId}
          x1={GRADIENT_LINE.x1 * gradientScaleX}
          y1={GRADIENT_LINE.y1 * gradientScaleY}
          x2={GRADIENT_LINE.x2 * gradientScaleX}
          y2={GRADIENT_LINE.y2 * gradientScaleY}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={GLOW_PURPLE} />
          <stop offset={0.504808} stopColor={GLOW_PINK} />
          <stop offset={0.817257} stopColor={GLOW_VIOLET} />
          <stop offset={1} stopColor={GLOW_PINK} />
        </linearGradient>
        <filter
          id={blurId}
          x="0"
          y="0"
          width={canvasWidth}
          height={canvasHeight}
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feGaussianBlur stdDeviation="3" />
        </filter>
      </defs>
      <g filter={`url(#${blurId})`}>
        <path
          className={strokeClass}
          d={pillPath}
          pathLength={100}
          stroke={`url(#${gradientId})`}
          strokeWidth={5}
          strokeLinecap="round"
        />
      </g>
    </svg>
  );
}
